// Record a value (T8): a torque, a gap, a pressure, with the limits shown.
// Out of limits asks for a note and offers to report a problem.
import { useState } from 'react';

import type { CheckResponse } from '../../api/generated/CheckResponse';
import type { ReadingResponse } from '../../api/generated/ReadingResponse';
import { ApiError } from '../../api/client';
import { Badge, Button, ErrorPanel, TextField } from '../../design/components';

export function limitsText(check: CheckResponse): string {
    const unit = check.unit ? ` ${check.unit}` : '';
    if (check.min !== null && check.max !== null) return `${String(check.min)}–${String(check.max)}${unit}`;
    if (check.min !== null) return `at least ${String(check.min)}${unit}`;
    if (check.max !== null) return `at most ${String(check.max)}${unit}`;
    return 'no limits';
}

export function isWithin(check: CheckResponse, value: number): boolean {
    return (check.min === null || value >= check.min) && (check.max === null || value <= check.max);
}

export function ReadingForm({
    check,
    reading,
    canAct,
    busy,
    error,
    onSave,
    onReportProblem,
}: {
    check: CheckResponse;
    reading: ReadingResponse | undefined;
    canAct: boolean;
    busy: boolean;
    error: unknown;
    onSave: (value: number, note: string | null) => void;
    onReportProblem: () => void;
}) {
    const [value, setValue] = useState(reading ? String(reading.value) : '');
    const [note, setNote] = useState(reading?.note ?? '');
    const parsed = Number.parseFloat(value);
    const valid = value.trim() !== '' && Number.isFinite(parsed);
    const outside = valid && !isWithin(check, parsed);
    const noteErrors = error instanceof ApiError ? error.fieldErrors('note') : [];
    return (
        <div className="sf-inset sf-stack">
            <div className="sf-row" style={{ justifyContent: 'space-between' }}>
                <strong>{check.name}</strong>
                <span className="sf-muted">Limits: {limitsText(check)}</span>
            </div>
            {reading ? (
                <div className="sf-row">
                    <Badge tone={reading.withinLimits ? 'success' : 'danger'}>
                        Read {String(reading.value)}
                        {check.unit ? ` ${check.unit}` : ''} {reading.withinLimits ? 'within limits' : 'outside limits'}
                    </Badge>
                    <span className="sf-muted">by {reading.read.displayName}</span>
                </div>
            ) : null}
            {canAct ? (
                <>
                    <TextField
                        label={`${check.name} reading${check.unit ? ` (${check.unit})` : ''}`}
                        value={value}
                        onChange={setValue}
                        inputMode="decimal"
                    />
                    {outside ? (
                        <div className="sf-note sf-note--pending" role="status">
                            That’s outside its limits. Add a note saying what you saw.
                        </div>
                    ) : null}
                    {outside || note ? (
                        <TextField label="Note" value={note} onChange={setNote} multiline errors={noteErrors} />
                    ) : null}
                    {error && noteErrors.length === 0 ? (
                        <ErrorPanel error={error} title="The reading wasn’t recorded." />
                    ) : null}
                    <div className="sf-row">
                        <Button
                            variant="primary"
                            disabled={!valid || busy || (outside && note.trim() === '')}
                            onClick={() => {
                                onSave(parsed, note.trim() === '' ? null : note.trim());
                            }}
                        >
                            {busy ? 'Saving…' : 'Save reading'}
                        </Button>
                        {outside ? <Button onClick={onReportProblem}>Report a problem</Button> : null}
                    </div>
                </>
            ) : null}
        </div>
    );
}
